import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { TableColumn } from '../../atoms/table/table.model';
import { TableComponent } from '../../atoms/table/table.component';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { NzTableQueryParams } from 'ng-zorro-antd/table';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  TranslocoDatePipe,
  TranslocoDecimalPipe,
} from '@jsverse/transloco-locale';
import { EVENTS_TABLE_DEFAULT_COLUMNS } from '../../../constants/table.constants';
import { EventDetails } from '@data';
import { Unit } from '../../../models/units.models';
import { UNITS } from '../../../constants/units.constants';
import { ActionsIndicatorComponent } from '../../molecules/indicators/actions-indicator.component';
import { IndicatorAction } from '@ui';
import { EventCenterIndicatorComponent } from '../../molecules/indicators/event-center-indicator.component';
import { EventGroupIndicatorComponent } from '../../molecules/indicators/event-group-indicator.component';
import { PermissionsService } from '../../../../app/core/services/permissions.service';

@Component({
  selector: 'ui-events-table',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TableComponent,
    TranslocoDirective,
    TranslocoDecimalPipe,
    TranslocoDatePipe,
    EventCenterIndicatorComponent,
    ActionsIndicatorComponent,
    EventGroupIndicatorComponent,
  ],
  template: `
    <ng-container *transloco="let t">
      @if (dataSource() && columns().length) {
        <ui-table
          [dataSource]="dataSource()"
          [columns]="columns()"
          [columnTemplates]="columnTemplates()"
          [scrollConfig]="scrollConfig()"
          [pageSize]="pageSize()"
          [pageIndex]="pageIndex()"
          [showPagination]="showPagination()"
          [showPageSizeChanger]="showPageSizeChanger()"
          [total]="total()"
          [showTotal]="total() ? totalTemplate : null"
          [loading]="loading()"
          [frontPagination]="false"
          [isMultiSelect]="true"
          (onQueryParams)="onQueryParamsChange($event)"
          (onChecked)="handleChecked($event)"
        />
      }

      <ng-template #centerTemplate let-row="row">
        <!-- cambiar -->
        <ui-event-center-indicator
          [state]="row.dateEnd!==null ? 'active' : 'danger'"
          [name]="row.centerName"
          [centerId]="row.centerId"
          [distributorId]="row.distributorId"
          [rsabtId]="row.rsabtId"
          [tsabtId]="row.tsabtId"
          [vtnId]="row.vtnId"
          (onCenter)="emitCenter($event)"
        />
      </ng-template>
      <ng-template #categoryTemplate let-row="row">
        <!-- cambiar -->
        <ui-event-group-indicator
          [group]="row.eventGroup"
          [type]="row.eventType"
        />
      </ng-template>
      <ng-template #dateTemplate let-row="row">
        {{
          row.datetime
            ? (row.datetime
              | translocoDate: { dateStyle: 'short', timeStyle: 'short' })
            : '--'
        }}
      </ng-template>
      <ng-template #actionsTemplate let-row="row">
        @if(hasWritePermission()){
          <div class="flex justify-end">
            <ui-actions-indicator
              (onAction)="emitAction(row)"
              [actions]="{
                archive: { disabled: getActiveState(row.unread) },
              }"
            />
          </div>
        }
      </ng-template>
      <ng-template #totalTemplate>
        {{ total() | translocoDecimal }}
        {{ t('DISTRIBUTION_NETWORK.TABLE.RESULTS') }}
      </ng-template>
    </ng-container>
  `,
})
export class EventsTableComponent {
  private translocoService = inject(TranslocoService);
  private destroyRef = inject(DestroyRef);
  protected readonly permissionsService = inject(PermissionsService);

  data = input.required<EventDetails[]>();
  total = input<number>(100);
  loading = input<boolean>(false);
  pageSize = input<number>(10);
  pageIndex = input<number>(1);
  showPagination = input<boolean>(true);
  showPageSizeChanger = input<boolean>(true);
  showCenterCode = input<boolean>(false);

  onQueryParams = output<{
    orderBy: string | undefined;
    order: string | undefined;
    pageSize: number;
    pageIndex: number;
  }>();
  onCenter = output<{
    distributorId: string;
    centerId: string;
    rsabtId: string;
    tsabtId: string;
    vtnId: string;
  }>();
  onCopyCenterId = output<string>();
  onRsabtId = output<string>();
  onAction = output<IndicatorAction>();
  onChecked = output<EventDetails[]>();

  actions = signal<Record<any, { disabled: boolean }>>({
    archive: { disabled: false },
  });

  dataSource = computed(() =>
    this.data().map((item) => {
      // try to use an explicit id if backend provides it
      const stableId =
        (item as any).id ??
        // fallback to a combination of stable fields (adjust fields to your model)
        `${item.id}`;
      return { ...item, id: stableId };
    }),
  );

  centerTemplate = viewChild.required<TemplateRef<any>>('centerTemplate');
  categoryTemplate = viewChild.required<TemplateRef<any>>('categoryTemplate');
  dateTemplate = viewChild.required<TemplateRef<any>>('dateTemplate');
  actionsTemplate = viewChild.required<TemplateRef<any>>('actionsTemplate');

  columnTemplates = computed(() => ({
    center: this.centerTemplate(),
    category: this.categoryTemplate(),
    date: this.dateTemplate(),
    actions: this.actionsTemplate(),
  }));

  columns = signal<TableColumn[]>([]);
  scrollConfigDefault = signal<{ x?: string | null; y?: string | null }>({
    x: '1000px',
    y: 'calc(100vh - 350px)',
  });
  scrollConfig = computed<{ x?: string | null; y?: string | null }>(() =>
     this.data()?.length ? this.scrollConfigDefault() : {},
  );
  DEFAULT_COLUMNS = computed<TableColumn[]>(() => {
    let cols = EVENTS_TABLE_DEFAULT_COLUMNS
      if (!this.hasWritePermission()) {
        cols = cols.filter(col => col.key !== 'actions');
      }
    return cols;
  });
  permissions = this.permissionsService.permissions;

  constructor() {
    
    effect(() => {
      this.setLocalizeColumns();
    });
  }

  /**
   * Returns true if the user has 'w' write permission.
   */
  hasWritePermission(): boolean {
    return this.permissions().some(p => p === 'sabt:events:w');
  }

  setLocalizeColumns() {
    this.translocoService
      .selectTranslation()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.columns.set(
          this.DEFAULT_COLUMNS().map((col) => ({
            ...col,
            title: this.translocoService.translate(col.title),
          })),
        );
      });
  }

  emitRsabtId(id: string) {
    this.onRsabtId.emit(id);
  }

  onQueryParamsChange(params: NzTableQueryParams): void {
    const { sort, pageSize, pageIndex } = params;
    const currentSort = sort.find((item) => item.value !== null);
    const orderBy = (currentSort && currentSort.key) || undefined;
    const order = (currentSort && currentSort.value) || undefined;
    this.onQueryParams.emit({ orderBy, order, pageSize, pageIndex });
  }

  emitCenter(event: {
    distributorId: string;
    centerId: string;
    rsabtId: string;
    tsabtId: string;
    vtnId: string;
  }) {
    this.onCenter.emit(event);
  }

  getUnit(unit: Unit) {
    return UNITS[unit];
  }

  getItemActions(disabled: boolean): Record<any, { disabled: boolean }> {
    return { archive: { disabled } };
  }

  emitAction(action: IndicatorAction) {
    this.onAction.emit(action);
  }

  handleChecked(indexs: any[]) {
    const items:EventDetails[] = this.data().filter((item) => indexs.includes(item.id));
    this.onChecked.emit(items);
  }

  getActiveState(read:boolean) {
    return  !read === true ? true : false;
  }
}
